import { ReactNode, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { useAlertContext, useConfirmationDialogContext } from "@shared";

import { Trans, useTranslation } from "@/translations";

import { FolderSelectionOutcome, MailMergeAccessState } from "../MailMergeAccess.types";
import { MailMergeFolderDialog } from "../MailMergeFolderDialog";
import { MAIL_MERGE_TEXT_EMPHASIS } from "../MailMergeTextEmphasis";
import { ResolvedGrant, useMailMergeAccess } from "./useMailMergeAccess";

interface FolderDialogState {
  expectedFolderPath: string;
  expectedFolderLeaf: string | null;
  rejectedFolderName: string | null;
  isSelecting: boolean;
  settle: (granted: boolean) => void;
}

/**
 * The interactive half of the mail merge (Serienbrief) folder grant: the explanatory dialogs that
 * have to precede every browser prompt, wrapped so both the user settings (Benutzereinstellungen)
 * and a business feature raise the same ones.
 *
 * Each dialog resolves to whether access is now in place, so a feature can gate its own flow on
 * {@link requestAccess} and carry on where it left off. The browser call always happens inside the
 * dialog's own click handler — that click is what supplies the transient user activation the picker
 * and the permission bar need. Every path settles, including the failing ones: a browser that
 * refuses the picker outright must not leave a caller awaiting forever.
 *
 * **A consumer must render the returned `folderDialog`.** It is the folder-selection dialog, which
 * is a component rather than a call into the shared confirmation dialog, because a refused selection
 * keeps it open for another attempt instead of sending the user back to the button.
 */
export function useMailMergeAccessPrompts() {
  const { t } = useTranslation();
  const { showConfirmationDialog, showOkDialog } = useConfirmationDialogContext();
  const { showError } = useAlertContext();
  const access = useMailMergeAccess();
  const { readCurrentGrant, chooseFolder, ensureAccess, resetAccess } = access;
  const [folderDialogState, setFolderDialogState] = useState<FolderDialogState | null>(null);

  const reportFailure = () => showError(t("mailMerge.access.errors.accessFailed"));

  /** Null means the grant could not be read and the user has already been told. */
  const readGrant = async (): Promise<ResolvedGrant | null> => {
    try {
      return await readCurrentGrant();
    } catch {
      reportFailure();
      return null;
    }
  };

  // Not showOkDialog: that takes a plain string, and the unsupported text carries markup the
  // <Trans> has to resolve. Passing showDiscardButton false gives the same single-button dialog.
  const showUnavailable = (state: MailMergeAccessState) =>
    new Promise<boolean>((resolve) => {
      showConfirmationDialog({
        dialogTitleText: t("mailMerge.access.unavailableDialog.title"),
        dialogText: (
          <Typography component="span">
            {state === MailMergeAccessState.Unsupported ? (
              <Trans i18nKey="mailMerge.access.descriptions.unsupported" components={MAIL_MERGE_TEXT_EMPHASIS} />
            ) : (
              <Trans i18nKey="mailMerge.access.descriptions.notConfigured" components={MAIL_MERGE_TEXT_EMPHASIS} />
            )}
          </Typography>
        ),
        showDiscardButton: false,
        onClose: () => resolve(false),
      });
    });

  const showDenied = () =>
    new Promise<boolean>((resolve) => {
      showOkDialog(t("mailMerge.access.deniedDialog.title"), t("mailMerge.access.deniedDialog.text"), () =>
        resolve(false)
      );
    });

  const requestFolderSelection = async (): Promise<boolean> => {
    // Read fresh: on the first interaction after a page load the configured path may not have
    // arrived yet, and the dialog is the one place it has to be named correctly.
    const grant = await readGrant();

    if (!grant?.expectedFolderPath) {
      return false;
    }

    const { expectedFolderPath, expectedFolderLeaf } = grant;

    return new Promise<boolean>((settle) => {
      setFolderDialogState({
        expectedFolderPath,
        expectedFolderLeaf,
        rejectedFolderName: null,
        isSelecting: false,
        settle,
      });
    });
  };

  const closeFolderDialog = (state: FolderDialogState, granted: boolean) => {
    setFolderDialogState(null);
    state.settle(granted);
  };

  const handleFolderSelect = () => {
    if (!folderDialogState) {
      return;
    }

    setFolderDialogState({ ...folderDialogState, isSelecting: true });

    // Called synchronously out of the dialog's click so the picker keeps its user activation.
    void chooseFolder(folderDialogState.expectedFolderLeaf)
      .then((result) => {
        if (result.outcome === FolderSelectionOutcome.Granted) {
          closeFolderDialog(folderDialogState, true);
          return;
        }

        // A refused or abandoned selection leaves the dialog open: the user is one click from
        // trying again, on the very path where they just went wrong.
        setFolderDialogState({
          ...folderDialogState,
          isSelecting: false,
          rejectedFolderName:
            result.outcome === FolderSelectionOutcome.NameMismatch
              ? result.selectedFolderName
              : folderDialogState.rejectedFolderName,
        });
      })
      .catch(() => {
        reportFailure();
        closeFolderDialog(folderDialogState, false);
      });
  };

  const requestRestore = async (): Promise<boolean> => {
    const resolved = await readGrant();

    if (!resolved) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      showConfirmationDialog({
        dialogTitleText: t("mailMerge.access.restoreDialog.title"),
        // The dialog renders its text inside a <p>, so only inline elements may go in here.
        dialogText: (
          <Stack component="span" spacing={2}>
            <Typography component="span" variant="body2">
              <Trans
                i18nKey="mailMerge.access.restoreDialog.text"
                values={{ expected: resolved.expectedFolderPath }}
                components={MAIL_MERGE_TEXT_EMPHASIS}
              />
            </Typography>
            <Box component="span" sx={{ p: 1.5, bgcolor: (theme) => theme.palette.fsw["grey-25"] }}>
              <Typography component="span" variant="body2">
                <Trans i18nKey="mailMerge.access.restoreDialog.hint" components={MAIL_MERGE_TEXT_EMPHASIS} />
              </Typography>
            </Box>
          </Stack>
        ),
        agreeText: t("mailMerge.access.restoreDialog.confirm"),
        discardText: t("common.close"),
        onClose: (confirmed) => {
          if (!confirmed) {
            resolve(false);
            return;
          }

          void (async () => {
            try {
              // The grant resolved above is handed over rather than re-read: the permission bar
              // needs this click's activation, and a fresh read could outlast it.
              const next = await ensureAccess(resolved);

              if (next === MailMergeAccessState.PermissionDenied) {
                await showDenied();
              }

              resolve(next === MailMergeAccessState.Ready);
            } catch {
              reportFailure();
              resolve(false);
            }
          })();
        },
      });
    });
  };

  const requestReset = () =>
    new Promise<void>((resolve) => {
      showConfirmationDialog({
        dialogTitleText: t("mailMerge.access.resetDialog.title"),
        dialogText: t("mailMerge.access.resetDialog.text"),
        agreeText: t("mailMerge.access.resetDialog.confirm"),
        agreeColor: "error",
        onClose: (confirmed) => {
          if (!confirmed) {
            resolve();
            return;
          }

          void (async () => {
            try {
              await resetAccess();
            } catch {
              reportFailure();
            } finally {
              resolve();
            }
          })();
        },
      });
    });

  /**
   * Brings the grant into place if it is not, showing whichever dialog the current state calls for.
   * Call it from the click handler, before any awaited request, and continue only on true.
   *
   * The state is re-read rather than taken from this render: on the first interaction after a page
   * load the queries may still be in flight, and a stale "nothing configured" would send the user
   * to a Supervisor over a setting that is in fact set.
   */
  const requestAccess = async (): Promise<boolean> => {
    const grant = await readGrant();

    if (!grant) {
      return false;
    }

    switch (grant.state) {
      case MailMergeAccessState.Ready:
        return true;
      case MailMergeAccessState.Unsupported:
      case MailMergeAccessState.NotConfigured:
        return showUnavailable(grant.state);
      case MailMergeAccessState.PermissionDenied:
        return showDenied();
      case MailMergeAccessState.PermissionLost:
        return requestRestore();
      case MailMergeAccessState.NotGranted:
      case MailMergeAccessState.FolderMismatch:
        return requestFolderSelection();
    }
  };

  const folderDialog: ReactNode = folderDialogState && (
    <MailMergeFolderDialog
      expectedFolderPath={folderDialogState.expectedFolderPath}
      rejectedFolderName={folderDialogState.rejectedFolderName}
      isSelecting={folderDialogState.isSelecting}
      onSelect={handleFolderSelect}
      onCancel={() => closeFolderDialog(folderDialogState, false)}
    />
  );

  return { access, folderDialog, requestAccess, requestFolderSelection, requestRestore, requestReset };
}
